import { pdkAxios } from '@/lib/pdk-axios.ts';
import { taskStoreV2 } from '@/models/stores/tasks-v2.ts';
import type { TaskableType } from '@/models/types.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export function useTasksMutations({
  taskableType,
  taskableId,
}: {
  taskableType: TaskableType;
  taskableId: string;
}) {
  const queryClient = useQueryClient();

  const assignUserToTaskMutation = useMutation({
    onMutate: async ({
      taskId,
      userId,
    }: {
      taskId: string;
      userId: string | null;
    }) => {
      await queryClient.cancelQueries({ queryKey: [taskableType, taskableId] });
      const previousTask = taskStoreV2.getById(taskId);
      taskStoreV2.updateAssignee({
        id: taskId,
        asigneeId: userId,
      });
      return { previousTask };
    },
    mutationFn: async ({
      taskId,
      userId,
    }: {
      taskId: string;
      userId: string | null;
    }) => {
      const url = userId
        ? `/tasks/${taskableId}/assign/${taskId}/assign/${userId}`
        : `/tasks/${taskableId}/assign/${taskId}/unassign`;

      const response = await pdkAxios
        .post(url, {
          // TODO -- should support not just user eventually (e.g. product_user/customer)
          assignee_type: 'user',
        })
        .catch((e) => {
          console.error('Error assigning user to task', e);
          throw e;
        });

      return response.data;
    },
    onError: (error, variables, context) => {
      console.error('Failed to assign user to task', error, variables);
      if (context?.previousTask) {
        taskStoreV2.updateAssignee({
          id: variables.taskId,
          asigneeId: context.previousTask.assignee_id,
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [taskableType, taskableId] });
    },
  });

  const setTaskToDoneMutation = useMutation({
    onMutate: async ({ taskId }: { taskId: string }) => {
      await queryClient.cancelQueries({ queryKey: [taskableType, taskableId] });
      const previousTask = taskStoreV2.getById(taskId);
      taskStoreV2.updateStatus({
        id: taskId,
        status: 'done',
      });
      return { previousTask };
    },
    mutationFn: async ({ taskId }: { taskId: string }) => {
      const response = await pdkAxios
        .post(`/tasks/${taskableId}/${taskId}/done`)
        .catch((e) => {
          console.error('Error setting task to done', e);
          throw e;
        });
      return response.data;
    },
    onError: (error, variables, context) => {
      console.error('Failed to set task to done', error, variables);
      if (context?.previousTask) {
        taskStoreV2.updateStatus({
          id: variables.taskId,
          status: context.previousTask.status,
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [taskableType, taskableId] });
    },
  });

  const unassignTaskMutation = useMutation({
    onMutate: async ({ taskId }: { taskId: string }) => {
      await queryClient.cancelQueries({ queryKey: [taskableType, taskableId] });
      const previousTask = taskStoreV2.getById(taskId);
      taskStoreV2.remove(taskId);
      return { previousTask };
    },
    mutationFn: async ({ taskId }: { taskId: string }) => {
      const response = await pdkAxios
        .post(`/tasks/${taskableId}/${taskId}/unassign`)
        .catch((e) => {
          console.error('Error unassigning task', e);
          throw e;
        });
      return response.data;
    },
    onError: (error, variables, context) => {
      console.error('Failed to unassign task', error, variables);
      if (context?.previousTask) {
        taskStoreV2.upsert(context.previousTask);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [taskableType, taskableId] });
    },
  });

  const setTaskToIncompleteMutation = useMutation({
    onMutate: async ({ taskId }: { taskId: string }) => {
      await queryClient.cancelQueries({ queryKey: [taskableType, taskableId] });
      const previousTask = taskStoreV2.getById(taskId);
      taskStoreV2.updateStatus({
        id: taskId,
        status: 'incomplete',
      });
      return { previousTask };
    },
    mutationFn: async ({ taskId }: { taskId: string }) => {
      const response = await pdkAxios
        .post(`/tasks/${taskableId}/${taskId}/incomplete`)
        .catch((e) => {
          console.error('Error setting task to incomplete', e);
          throw e;
        });
      return response.data;
    },
    onError: (error, variables, context) => {
      console.error('Failed to set task to incomplete', error, variables);
      if (context?.previousTask) {
        taskStoreV2.updateStatus({
          id: variables.taskId,
          status: context.previousTask.status,
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [taskableType, taskableId] });
    },
  });

  return {
    assignUserToTaskMutation,
    setTaskToDoneMutation,
    unassignTaskMutation,
    setTaskToIncompleteMutation,
  };
}
